import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Venue } from '../types/venue';

type VenueResponse = Venue;
type DefaultError = unknown;

type UseVenueParams = {
  venueId?: string;
  /**
   * Comma-separated list of relations, e.g. 'spaces,toastRestaurant,toastPrivateDiningMenu,toastCorporateDiningMenu'
   */
  include?: string;
  toastInclude?: string;
  /** Override default enabled behavior */
  enabled?: boolean;
};

/**
 * useVenue
 *
 * @example
 * const venueQuery = useVenue(
 *   {
 *     venueId,
 *     include: 'spaces,toastRestaurant,toastPrivateDiningMenu,toastCorporateDiningMenu',
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useVenue<TData = VenueResponse, TError = DefaultError>(
  { venueId, include, toastInclude, enabled = true }: UseVenueParams = {},
  options?: Omit<
    UseQueryOptions<VenueResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['venue', venueId, include];

  return useQuery<VenueResponse, TError, TData>({
    queryKey,
    // don’t run until we actually have an ID
    enabled: enabled && !!venueId,
    queryFn: async () => {
      if (!venueId) {
        throw new Error('venueId is required to fetch a venue');
      }

      return service.getVenue(venueId, {
        ...(include ? { include } : {}),
        ...(toastInclude ? { toastInclude } : {}),
      });
    },
    ...(options as any),
  });
}
